// create-action-from-gap.ts
//
// Writes a real Action Log row from a measured attribute gap.
//
// This was inline in antenna-dashboard while Attribute rankings lived on Brand
// Monitoring. The feature now renders on the Deep Signal Competitive tab, so
// the write path moved here rather than being copied: one ingest call, one set
// of composed copy, whichever page mounts the section.
//
// Deviation worth knowing (carried over verbatim from the original): the copy
// on that action is composed deterministically from the measurement (attribute,
// leader, both scores), NOT written by the LLM generator. The existing
// generation path rebuilds the whole suggested list from brand context and
// cannot target one gap, so using it here would not produce an action for this
// gap. Composing from measured facts is honest; inventing a recommendation
// would not be.
import { toast } from "sonner";
import { fixActionsService } from "./fix-actions-service";

/**
 * What the host returns after matching or writing an action. The drawer reads
 * impact and effort from HERE, the record itself, never from anything it
 * derives locally (phase 12e addendum).
 */
export interface CreatedAction {
  id: string;
  effort?: string | null;
  impactLevel?: string | null;
}

export interface GapDescriptor {
  attribute: string;
  definition: string;
  leaderBrand: string;
  clientScore: number | null;
  leaderScore: number | null;
  evidenceQuotes: { text: string; model: string }[];
  defend: boolean;
}

/** Page-supplied context: which brand and run the gap was measured on. */
export interface GapActionContext {
  brand: string;
  brandId: string;
  modelsUsed: string[];
}

/**
 * Uses the existing ingest path (the same one the Recommended Actions
 * "Implement" button uses), so the action that appears in Action Items is a
 * genuine row, not a mock.
 */
export async function createActionFromGap(
  gap: GapDescriptor,
  ctx: GapActionContext,
): Promise<CreatedAction | null> {
  const label = gap.attribute.charAt(0).toUpperCase() + gap.attribute.slice(1);
  const title = gap.defend
    ? `Defend the ${label.toLowerCase()} lead`
    : `Close the ${label.toLowerCase()} gap against ${gap.leaderBrand}`;
  const scores = gap.leaderScore != null && gap.clientScore != null
    ? ` ${gap.leaderBrand} scores ${gap.leaderScore}; you score ${gap.clientScore}.`
    : "";
  try {
    const res = await fixActionsService.ingestActionLog({
      sourceModule: "competitive-intelligence",
      sourceId: `attr:${gap.attribute}`,
      title,
      description:
        `${gap.definition}.${scores} Evidence: ` +
        (gap.evidenceQuotes.map((q) => `"${q.text}" (${q.model})`).join(" ") || "none kept."),
      priority: gap.defend ? "Medium" : "High",
      // No effort is sent. Nothing measured here knows how much work this
      // is, and stamping "Medium" would put an invented value on the record
      // that the drawer would then render back as fact (phase 12e).
      impactScore: gap.defend ? 50 : 80,
      brand: ctx.brand,
      modelsUsed: ctx.modelsUsed,
      metadata: {
        from: "competitive_intelligence_attribute_gap",
        attribute: gap.attribute,
        leaderBrand: gap.leaderBrand,
        clientScore: gap.clientScore,
        leaderScore: gap.leaderScore,
        defend: gap.defend,
      },
      // Written by opening an evidence drawer: held out of the main Action
      // Items list until the user opens or accepts it.
      origin: "competitive-suggested",
      brandId: ctx.brandId,
    });
    const action = (res as any)?.action ?? null;
    const id = action?.id;
    if (id == null) return null;
    // Only announce a row that was actually written. A deduped hit is the
    // drawer matching the row it wrote earlier, not a new action.
    if (!(res as any)?.deduped) toast.success("Added to Action Log");
    return {
      id: String(id),
      effort: action?.effort ?? null,
      impactLevel: action?.impactLevel ?? null,
    };
  } catch (e) {
    console.error(e);
    toast.error("Could not create that action");
    return null;
  }
}
